import {
  DiabetesBgmSystem,
  DiabetesCgmSensorType,
  DiabetesCgmSystem,
  DiabetesHypoglycemicEvent,
  DiabetesInsulinTreatmentType,
  DiabetesMdiQualifyingCondition,
  DiabetesPumpSystem,
  DiabeticFootCondition,
  NebulizerMedication,
  WheelchairsAndScootersAdlDifficulty,
  WheelchairsAndScootersDeviceType,
  WheelchairsAndScootersMobilityLimitationReason,
  WheelchairsAndScootersMradlBenefit,
  WheelchairsAndScootersNewWheelchairReasonCategory,
  WoundSuctionOrderInput,
  WoundSuctionUlcerType,
} from "../__generated__/graphql";

export const DEFAULT_WOUND_PREFERENCE_ID = 3;
export const DEFAULT_MEDICARE_WOUND_PREFERENCE_ID = 2;
export const NUM_WOUNDS = 4;
export const NUM_MISCELLANEOUS_DIAGNOSES = 4;
export const NUM_MISCELLANEOUS_LINE_ITEMS = 4;

export const CPAP_MACHINE_TYPE_LABELS: Record<string, string> = {
  match_existing: "Match Existing",
  cpap: "CPAP",
  auto_cpap: "Auto CPAP",
  bilevel: "Bi-Level PAP",
  auto_bilevel: "Auto Bi-Level PAP",
  bipap_st: "BiPAP ST",
};

export const CPAP_HUMIDIFIER_LABELS: Record<string, string> = {
  heated: "Heated Humidifier",
  non_heated: "Non-heated Humidifier",
};

export const CPAP_DEFAULT_DIAGNOSES = [
  { code: "G47.33", description: "Obstructive Sleep Apnea" },
  { code: "G47.31", description: "Central Sleep Apnea" },
  { code: "G47.36", description: "Complex sleep apnea" },
  { code: "J44.9", description: "Chronic obstructive pulmonary disease" },
];

export const CPAP_MASK_TYPE_LABELS: Record<string, string> = {
  match_existing: "Match Existing",
  none: "None",
  full_face: "Full Face",
  nasal: "Nasal",
  nasal_pillow: "Nasal Pillow",
};

export const CPAP_MASK_TYPE_FREQUENCY: Record<string, string> = {
  full_face: "1 per 3 months",
  nasal: "1 per 3 months",
  nasal_pillow: "1 per 3 months",
};

export const CPAP_MASK_REFILL_FREQUENCY: Record<string, string> = {
  "Full-Face Mask Cushion Replacement": "1 per 1 month",
  "Nasal Mask Cushion Replacement": "2 per 1 month",
  "Nasal Pillow Cushion Replacement": "2 per 1 month",
};

// TODO: Move to form_items table (e.g. replacement_quantity + replacement_period_months columns)
// once supply frequencies need to be configurable or more CPAP products are added
export const CPAP_SUPPLY_FREQUENCY: Record<string, string> = {
  Headgear: "1 per 6 months",
  Chinstrap: "1 per 6 months",
  "Tubing, 6ft Std.": "1 per 3 months",
  "Tubing, Heated": "1 per 3 months",
  "Filters, Disposable": "2 per 1 month",
  "Filters, Non-Disposable": "1 per 6 months",
  "Humidifier Chamber": "1 per 6 months",
};

/**
 * Every drug the Nebulizer form can record, by stored member.
 *
 * Mirrors `_MEDICATION_LABELS` in `services/orders/display_model/nebulizer.py`,
 * which is what the ops display prints. The provider order form, the SWO and
 * the Progress Note summary all read this map, so the same order never names
 * the drug differently depending on who is looking at it.
 *
 * Keyed by the member rather than by string, so adding one to the enum without
 * a label here is a compile error instead of a blank cell on a document.
 */
export const NEBULIZER_MEDICATION_LABELS: Record<NebulizerMedication, string> =
  {
    [NebulizerMedication.AlbuterolIpratropium]:
      "Albuterol ipratropium (Combivent, DuoNeb)",
    [NebulizerMedication.Acetylcysteine]:
      "Acetylcysteine (Acetadote, NAC, Cetylev)",
    [NebulizerMedication.Arformoterol]: "Arformoterol (Brovana)",
    [NebulizerMedication.DornaseAlfa]: "Dornase alfa (Pulmozyme)",
    [NebulizerMedication.Formoterol]: "Formoterol (Perforomist, Symbicort)",
    [NebulizerMedication.Pentamidine]: "Pentamidine (Nebupent)",
    [NebulizerMedication.Tobramycin]: "Tobramycin (Tobrex)",
    [NebulizerMedication.AlbuterolSulfate]:
      "Albuterol sulfate (ProAir HFA, Proventil HFA, ProAir RespiClick)",
    [NebulizerMedication.Budesonide]:
      "Budesonide (Pulmicort, Entocort, Uceris)",
    [NebulizerMedication.Cromolyn]: "Cromolyn (Gastrocrom)",
    [NebulizerMedication.IpratropiumBromide]: "Ipratropium bromide (Atrovent)",
    [NebulizerMedication.Levalbuterol]:
      "Levalbuterol (Xopenex, Xopenex HFA, Xopenex Concentrate)",
    [NebulizerMedication.Metaproterenol]: "Metaproterenol (Alupent)",
    [NebulizerMedication.Revefenacin]: "Revefenacin (Yupelri)",
    [NebulizerMedication.Treprostinil]:
      "Treprostinil (Tyvaso, Remodulin, Orenitram)",
    [NebulizerMedication.Iloprost]: "Iloprost (Ventavis)",
    [NebulizerMedication.HypertonicSaline]:
      "Hypertonic saline (HyperSal, PulmoSal)",
    [NebulizerMedication.Ensifentrine]: "Ensifentrine (Ohtuvayre)",
    // Never rendered: an "Other" order carries its name in `otherMedication`.
    [NebulizerMedication.Other]: "Other",
  };

/**
 * The wound-status values `DebridementSelector` writes onto a wound row.
 *
 * The strings are the stored values, not labels -- the selector's own label for
 * SURGICAL changes with the `WOUND_STATUS_ROLLOUT` flag while the value does
 * not. Named here because the Wound Suction interview routes on them, and a
 * status it does not recognise reads as "no status recorded", which turns a
 * covered order into an unfinished one with nothing on screen saying why.
 */
const WOUND_STATUS = {
  DEBRIDED: "Debrided",
  UNDEBRIDED: "Undebrided",
  SURGICAL: "Surgical",
} as const;

/**
 * The statuses that satisfy the Wound Suction debridement requirement.
 *
 * Mirrors `_QUALIFYING_WOUND_STATUSES` in
 * `services/orders/medical_necessity/wound_suction.py`.
 */
export const QUALIFYING_WOUND_STATUSES: readonly string[] = [
  WOUND_STATUS.DEBRIDED,
  WOUND_STATUS.SURGICAL,
];

/**
 * Display copy for the Wound Suction ulcer types.
 *
 * Keyed by the member, so adding one to the enum without a label here is a
 * compile error rather than a raw `PRESSURE_STAGE_3_OR_4` on the form. The graph
 * carries the routing; this carries the wording.
 *
 * Shared with the PN summary section: the ulcer type is one of the interview
 * questions the note prints back, and it is the same clinical phrase the
 * provider read when they answered it.
 */
export const WOUND_SUCTION_ULCER_LABELS: Record<WoundSuctionUlcerType, string> =
  {
    [WoundSuctionUlcerType.PressureStage_3Or_4]:
      "Chronic stage 3 or 4 pressure ulcer",
    [WoundSuctionUlcerType.Neuropathic]: "Neuropathic ulcer",
    [WoundSuctionUlcerType.VenousOrArterialInsufficiency]:
      "Venous or arterial insufficiency ulcer",
    [WoundSuctionUlcerType.ChronicMixedEtiology]:
      "Chronic (present for at least 30 days) ulcer of mixed etiology",
    [WoundSuctionUlcerType.NoneOfTheAbove]: "None of the above",
  };

/**
 * A boolean question's two answers, in the order the buttons are drawn.
 *
 * A pair rather than a list, and each half pinned to one literal, so both
 * booleans are present by construction. That is what lets the lookup below be
 * total: a map missing one value would otherwise print the wrong word on a
 * signed document rather than failing.
 */
type BooleanAnswer<V extends boolean> = { value: V; label: string };
type BooleanAnswerPair =
  | readonly [BooleanAnswer<false>, BooleanAnswer<true>]
  | readonly [BooleanAnswer<true>, BooleanAnswer<false>];

/**
 * Wound Suction interview fields whose boolean answer reads as something other
 * than Yes/No.
 *
 * "Is this a new order or continuing an existing therapy?" has no Yes. Every
 * surface that prints an interview answer -- the form's buttons, the ops order
 * display, the PN summary -- switches on the answer's type, so without this a
 * provider picks New and ops reads No.
 *
 * `satisfies` pins the keys to real columns: a typo would otherwise compile and
 * silently fall back to Yes/No, which is the bug this map exists to fix.
 *
 * Mirrored by `_BOOLEAN_ANSWER_LABELS` in
 * `services/orders/display_model/wound_suction.py`. Nothing enforces the two
 * agree -- keep them in the same commit.
 */
export const WOUND_SUCTION_BOOLEAN_ANSWERS = {
  isContinuingExistingTherapy: [
    { value: false, label: "New" },
    { value: true, label: "Continuing" },
  ],
} as const satisfies Partial<
  Record<keyof WoundSuctionOrderInput, BooleanAnswerPair>
>;

/** The Yes/No buttons a boolean interview question gets unless it names its own. */
export const DEFAULT_BOOLEAN_ANSWERS: BooleanAnswerPair = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

/** The two answer buttons a boolean interview field is drawn with, in order. */
export const woundSuctionBooleanAnswers = (field: string): BooleanAnswerPair =>
  (WOUND_SUCTION_BOOLEAN_ANSWERS as Record<string, BooleanAnswerPair>)[field] ??
  DEFAULT_BOOLEAN_ANSWERS;

/** One boolean interview answer, in the words the provider chose it by. */
export const woundSuctionBooleanAnswerText = (
  field: string,
  answer: boolean,
): string => {
  const [first, second] = woundSuctionBooleanAnswers(field);
  return first.value === answer ? first.label : second.label;
};

/**
 * Every device label, shared by the form and the PN/SWO summary so wording
 * never diverges (the form itself uses a shorter "Other:" label instead of the Other* entries here).
 */
export const WHEELCHAIRS_AND_SCOOTERS_DEVICE_LABELS: Record<
  WheelchairsAndScootersDeviceType,
  string
> = {
  [WheelchairsAndScootersDeviceType.CushionOnly]: "Order cushion only",
  [WheelchairsAndScootersDeviceType.StandardManual]:
    "Standard manual wheelchair",
  [WheelchairsAndScootersDeviceType.LightweightManual]:
    "Lightweight wheelchair",
  [WheelchairsAndScootersDeviceType.BariatricManual]:
    "Bariatric manual wheelchair",
  [WheelchairsAndScootersDeviceType.Transport]: "Transport wheelchair",
  [WheelchairsAndScootersDeviceType.BariatricTransport]:
    "Bariatric transport wheelchair",
  [WheelchairsAndScootersDeviceType.PowerAssist]: "Power assist system",
  [WheelchairsAndScootersDeviceType.PowerWheelchair]: "Power wheelchair",
  [WheelchairsAndScootersDeviceType.PortablePowerWheelchair]:
    "Portable power wheelchair",
  [WheelchairsAndScootersDeviceType.Scooter]: "Power scooter",
  [WheelchairsAndScootersDeviceType.OtherWheelchair]: "Other wheelchair",
  [WheelchairsAndScootersDeviceType.OtherScooter]: "Other scooter",
};

/**
 * Fixed `productName` per device type (mirrors `DEVICE_TYPE_ITEM_NAMES`), so the
 * device row stays distinct from its add-ons after an ops rename.
 */
export const WHEELCHAIRS_AND_SCOOTERS_DEVICE_ITEM_NAMES: Partial<
  Record<WheelchairsAndScootersDeviceType, string>
> = {
  [WheelchairsAndScootersDeviceType.CushionOnly]: "Wheelchair Cushion",
  [WheelchairsAndScootersDeviceType.StandardManual]:
    "Standard Manual Wheelchair",
  [WheelchairsAndScootersDeviceType.LightweightManual]:
    "Lightweight Wheelchair",
  [WheelchairsAndScootersDeviceType.BariatricManual]:
    "Bariatric Manual Wheelchair",
  [WheelchairsAndScootersDeviceType.Transport]: "Transport Wheelchair",
  [WheelchairsAndScootersDeviceType.BariatricTransport]:
    "Bariatric Transport Wheelchair",
  [WheelchairsAndScootersDeviceType.PowerAssist]: "Power Assist System",
  [WheelchairsAndScootersDeviceType.PowerWheelchair]: "Power Wheelchair",
  [WheelchairsAndScootersDeviceType.PortablePowerWheelchair]:
    "Portable Power Wheelchair",
  [WheelchairsAndScootersDeviceType.Scooter]: "Power Scooter",
};

/**
 * Escape-hatch device types with no catalog row. Mirrors `OTHER_DEVICE_TYPES`
 * in `validate/wheelchairs_and_scooters.py`.
 */
export const WHEELCHAIRS_AND_SCOOTERS_OTHER_DEVICE_TYPES = new Set([
  WheelchairsAndScootersDeviceType.OtherWheelchair,
  WheelchairsAndScootersDeviceType.OtherScooter,
]);

/**
 * Display copy for the Wheelchairs & Scooters mobility-limitation checkboxes.
 *
 * Mirrors `_MOBILITY_LIMITATION_LABELS` in
 * `services/orders/display_model/wheelchairs_and_scooters.py`, so the
 * medical-necessity question reads the same on the provider form, the PN
 * summary, and the ops display.
 */
export const WHEELCHAIRS_AND_SCOOTERS_MOBILITY_LIMITATION_LABELS: Record<
  WheelchairsAndScootersMobilityLimitationReason,
  string
> = {
  [WheelchairsAndScootersMobilityLimitationReason.SevereDifficultyWalking]:
    "Has severe difficulty walking",
  [WheelchairsAndScootersMobilityLimitationReason.HighFallRisk]:
    "Has a high risk of falling",
  [WheelchairsAndScootersMobilityLimitationReason.ReliesOnCaregiverAssistance]:
    "Relies on caregiver assistance",
  [WheelchairsAndScootersMobilityLimitationReason.TooWeakToWalkIndependently]:
    "Is too weak to walk safely independently",
  [WheelchairsAndScootersMobilityLimitationReason.PoorBalance]:
    "Has poor balance",
  [WheelchairsAndScootersMobilityLimitationReason.NonAmbulatory]:
    "Is non-ambulatory",
};

/** Mirrors `_NEW_WHEELCHAIR_REASON_CATEGORY_LABELS` in `display_model/wheelchairs_and_scooters.py`. */
export const WHEELCHAIRS_AND_SCOOTERS_NEW_WHEELCHAIR_REASON_CATEGORY_LABELS: Record<
  WheelchairsAndScootersNewWheelchairReasonCategory,
  string
> = {
  [WheelchairsAndScootersNewWheelchairReasonCategory.IrreparablyDamaged]:
    "Irreparably damaged or broken beyond repair",
  [WheelchairsAndScootersNewWheelchairReasonCategory.LostOrStolen]:
    "Lost or stolen",
  [WheelchairsAndScootersNewWheelchairReasonCategory.PatientConditionChanged]:
    "Patient condition changed",
  [WheelchairsAndScootersNewWheelchairReasonCategory.DeviceNoLongerMedicallyAppropriate]:
    "Device no longer medically appropriate",
  [WheelchairsAndScootersNewWheelchairReasonCategory.Other]: "Other",
};

/**
 * Display copy for the Wheelchairs & Scooters ADL-difficulty checkboxes.
 *
 * Mirrors `_ADL_LABELS` in `services/orders/display_model/wheelchairs_and_scooters.py`,
 * so the medical-necessity question reads the same on the provider form as it
 * does on the ops display.
 */
export const WHEELCHAIRS_AND_SCOOTERS_ADL_LABELS: Record<
  WheelchairsAndScootersAdlDifficulty,
  string
> = {
  [WheelchairsAndScootersAdlDifficulty.WalkingIndependently]:
    "Walking independently",
  [WheelchairsAndScootersAdlDifficulty.Toileting]: "Toileting",
  [WheelchairsAndScootersAdlDifficulty.Feeding]: "Feeding",
  [WheelchairsAndScootersAdlDifficulty.Dressing]: "Dressing",
  [WheelchairsAndScootersAdlDifficulty.Grooming]: "Grooming",
  [WheelchairsAndScootersAdlDifficulty.Bathing]: "Bathing",
};

/**
 * Display copy for the Wheelchairs & Scooters MRADL-benefit checkboxes.
 *
 * Mirrors `_MRADL_LABELS` in `services/orders/display_model/wheelchairs_and_scooters.py`.
 */
export const WHEELCHAIRS_AND_SCOOTERS_MRADL_LABELS: Record<
  WheelchairsAndScootersMradlBenefit,
  string
> = {
  [WheelchairsAndScootersMradlBenefit.RegainIndependence]:
    "Regain independence",
  [WheelchairsAndScootersMradlBenefit.LessCaregiverAssistance]:
    "Require less caregiver assistance",
  [WheelchairsAndScootersMradlBenefit.MobileLongerDistances]:
    "Be mobile around the home for longer distances",
  [WheelchairsAndScootersMradlBenefit.ReducedFallRisk]:
    "Have reduced fall risk",
  [WheelchairsAndScootersMradlBenefit.CompleteMradlsSitting]:
    "Be able to complete MRADLs while sitting",
};

/** The design's shorter wording, which is not the billable catalog name. */
export const BGM_SYSTEM_LABELS: Record<DiabetesBgmSystem, string> = {
  [DiabetesBgmSystem.TrueMetrixPro]: "True Metrix Pro",
  [DiabetesBgmSystem.AccuChekGuide]: "Roche Accu-Chek Guide",
  [DiabetesBgmSystem.OnetouchVerioFlex]: "OneTouch Verio Flex",
  [DiabetesBgmSystem.FreestyleLite]: "FreeStyle Lite",
};

export const CGM_SYSTEM_LABELS: Record<DiabetesCgmSystem, string> = {
  [DiabetesCgmSystem.DexcomG7]: "Dexcom G7",
  [DiabetesCgmSystem.FreestyleLibre_3]: "FreeStyle Libre 3 Plus",
};

export const PUMP_SYSTEM_LABELS: Record<DiabetesPumpSystem, string> = {
  [DiabetesPumpSystem.BetaBionicsIlet]: "Beta Bionics iLet",
  [DiabetesPumpSystem.MedtronicMinimed]: "Medtronic MiniMed",
  [DiabetesPumpSystem.TandemMobi]: "Tandem Mobi",
  [DiabetesPumpSystem.TandemTslimX2]: "Tandem t:slim X2",
};

/** Shared with the form: the written order must name which duration to ship. */
export const SENSOR_TYPE_LABELS: Record<DiabetesCgmSensorType, string> = {
  [DiabetesCgmSensorType.TenDay]: "10-day sensors",
  [DiabetesCgmSensorType.FifteenDay]: "15-day sensors",
};

/**
 * The L33822 hypoglycemia criteria, worded as the LCD words them.
 *
 * The provider attests to these, and the Progress Note prints the same
 * sentences back for signature, so both read from here.
 */
export const DIABETES_HYPOGLYCEMIC_EVENT_LABELS: Record<
  DiabetesHypoglycemicEvent,
  string
> = {
  [DiabetesHypoglycemicEvent.Level_2Recurrent]:
    "Recurrent (more than one) level 2 hypoglycemic events (glucose below " +
    "54 mg/dL) that persist despite more than one attempt to adjust " +
    "medications or modify the diabetes treatment plan",
  [DiabetesHypoglycemicEvent.Level_3History]:
    "A history of one level 3 hypoglycemic event (glucose below 54 mg/dL) " +
    "with altered mental or physical state requiring third-party assistance " +
    "for treatment",
  [DiabetesHypoglycemicEvent.None]: "None of the above",
};

export const DIABETES_INSULIN_TREATMENT_TYPE_LABELS: Record<
  DiabetesInsulinTreatmentType,
  string
> = {
  [DiabetesInsulinTreatmentType.InsulinPump]: "Insulin pump",
  [DiabetesInsulinTreatmentType.MultipleDailyInjections]:
    "Multiple daily injections",
  [DiabetesInsulinTreatmentType.Other]: "Other",
};

/**
 * The order the design lists these in. Held explicitly rather than taken from
 * `Object.values`, which is alphabetical: it put "None of the above" third of
 * six, in the middle of the list rather than at the end.
 */
export const DIABETES_HYPOGLYCEMIC_EVENT_ORDER: DiabetesHypoglycemicEvent[] = [
  DiabetesHypoglycemicEvent.Level_2Recurrent,
  DiabetesHypoglycemicEvent.Level_3History,
  DiabetesHypoglycemicEvent.None,
];

/** The L33794 criterion C conditions, any one of which qualifies. */
export const DIABETES_MDI_QUALIFYING_CONDITION_LABELS: Record<
  DiabetesMdiQualifyingCondition,
  string
> = {
  [DiabetesMdiQualifyingCondition.Hba1CAbove_7]: "HbA1c above 7%",
  [DiabetesMdiQualifyingCondition.DawnPhenomenon]:
    "Dawn phenomenon with fasting blood sugars frequently above 200 mg/dL",
  [DiabetesMdiQualifyingCondition.RecurringHypoglycemia]:
    "Recurring hypoglycemia",
  [DiabetesMdiQualifyingCondition.SevereGlycemicExcursions]:
    "Severe glycemic excursions",
  [DiabetesMdiQualifyingCondition.WideFluctuationsBeforeMealtime]:
    "Wide fluctuations in blood glucose before mealtime",
  [DiabetesMdiQualifyingCondition.None]: "None of the above",
};

/** As above: the design's order, with "None of the above" last. */
export const DIABETES_MDI_QUALIFYING_CONDITION_ORDER: DiabetesMdiQualifyingCondition[] =
  [
    DiabetesMdiQualifyingCondition.Hba1CAbove_7,
    DiabetesMdiQualifyingCondition.DawnPhenomenon,
    DiabetesMdiQualifyingCondition.RecurringHypoglycemia,
    DiabetesMdiQualifyingCondition.SevereGlycemicExcursions,
    DiabetesMdiQualifyingCondition.WideFluctuationsBeforeMealtime,
    DiabetesMdiQualifyingCondition.None,
  ];

/**
 * Prosthetics option tokens as the provider saw them on the form.
 *
 * Mirrors `_OPTION_LABELS` in `services/orders/display_model/prosthetics.py`,
 * so a stored token reads the same on the provider form, the SWO, and the ops
 * display. Only the fitting choices live here -- the medical-necessity tokens
 * stay in the form's own catalog, since no shared document renders them.
 *
 * Unmapped values fall through to themselves: these columns have no CHECK.
 */
export const PROSTHETICS_OPTION_LABELS: Record<string, string> = {
  ABOVE_KNEE: "Above knee",
  BELOW_KNEE: "Below knee",
  EIGHT_TO_TWELVE_INCHES: '8-12"',
  OVER_TWELVE_INCHES: '12+"',
  LOCKING_PIN: "Locking pin",
  VACUUM_OR_OTHER: "Vacuum or other",
  RIGHT: "Right",
  LEFT: "Left",
  BILATERAL: "Bilateral",
};

export const prostheticsOptionLabel = (value: string): string =>
  PROSTHETICS_OPTION_LABELS[value] ?? value;

/**
 * `prosthetics_line_items.subcategory`. Mirrors `ProstheticsSubcategory` in
 * schema/prosthetics_order.py, which is the enforcing copy.
 *
 * Declared here rather than derived from the form's catalog because the label
 * map below has to be exhaustive over it, and `packages/shared-components`
 * cannot import app code. `prostheticsCatalog.ts` re-exports it and pins its
 * own `PROSTHETICS_SUBCATEGORY` to it, so the two cannot drift.
 */
export type ProstheticsSubcategory =
  | "MASTECTOMY"
  | "LOWER_LIMB"
  | "TOE_FILLERS"
  | "OTHER";

/**
 * `prosthetics_line_items.subcategory` as the provider saw the form sectioned,
 * in the order the form sections them.
 *
 * Here rather than in the form's own catalog because the Progress Note summary
 * groups its item list by these, and `packages/shared-components` cannot import
 * app code. `prostheticsCatalog.ts` re-exports it, so the form reads one copy.
 *
 * Exhaustive, so a new subcategory cannot be added without labelling it.
 */
export const PROSTHETICS_SUBCATEGORY_LABELS: Record<
  ProstheticsSubcategory,
  string
> = {
  MASTECTOMY: "Mastectomy",
  LOWER_LIMB: "Lower Limb",
  TOE_FILLERS: "Toe Fillers",
  OTHER: "Other",
};

/** Falls through to the raw token: the column has no CHECK. */
export const prostheticsSubcategoryLabel = (subcategory: string): string =>
  PROSTHETICS_SUBCATEGORY_LABELS[subcategory as ProstheticsSubcategory] ??
  subcategory;

/**
 * Toe filler diabetic foot conditions. Mirrors `_OPTION_LABELS` in
 * `display_model/prosthetics.py`.
 */
export const DIABETIC_FOOT_CONDITION_LABELS: Record<
  DiabeticFootCondition,
  string
> = {
  [DiabeticFootCondition.Amputation]:
    "History of partial or complete amputation of the foot",
  [DiabeticFootCondition.FootUlceration]: "History of previous foot ulceration",
  [DiabeticFootCondition.PreUlcerativeCallus]:
    "History of pre-ulcerative callus",
  [DiabeticFootCondition.PeripheralNeuropathyWithCallus]:
    "Peripheral neuropathy with evidence of callus formation",
  [DiabeticFootCondition.FootDeformity]: "Foot deformity",
  [DiabeticFootCondition.PoorCirculation]: "Poor circulation",
  [DiabeticFootCondition.None]: "None of the above",
};

export const diabeticFootConditionLabel = (value: string): string =>
  DIABETIC_FOOT_CONDITION_LABELS[value as DiabeticFootCondition] ?? value;
